import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Instagram, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: 'general',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/api/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="contact-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center glass p-12 max-w-lg mx-4"
        >
          <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="font-heading text-2xl text-text-primary mb-4">
            Message Received
          </h2>
          <p className="text-text-secondary mb-6">
            Thank you for reaching out. We'll get back to you within 24-48 hours.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                company: '',
                inquiry_type: 'general',
                message: ''
              });
            }}
            className="btn-secondary"
          >
            Send Another Message
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-text-primary mb-6">
              Get In <span className="text-primary text-glow-red">Touch</span>
            </h1>
            <p className="text-text-secondary text-base md:text-lg">
              Have a question or want to work with us? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-8 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Your company"
                    data-testid="contact-company"
                  />
                </div>

                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Inquiry Type *
                  </label>
                  <select
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary focus:border-primary focus:outline-none transition-colors"
                    data-testid="contact-inquiry-type"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="talent">Talent Representation</option>
                    <option value="social">Social Media Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your project or inquiry..."
                    data-testid="contact-message"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              <div className="glass p-8">
                <h3 className="font-heading text-xl text-text-primary mb-6">
                  Contact Information
                </h3>
                
                <div className="space-y-6">
                  <a 
                    href="mailto:management@eldiablo.com"
                    className="flex items-center gap-4 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Mail className="w-6 h-6" />
                    <span>management@eldiablo.com</span>
                  </a>
                </div>
              </div>

              <div className="glass p-8">
                <h3 className="font-heading text-xl text-text-primary mb-6">
                  Follow Us
                </h3>
                
                <div className="flex gap-4">
                  <a 
                    href="https://www.instagram.com/eldiablomanagement" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-surface border border-border hover:border-primary hover:text-primary transition-colors text-text-secondary"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://x.com/ElDiabloMM" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-surface border border-border hover:border-primary hover:text-primary transition-colors text-text-secondary"
                    aria-label="X"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                </div>
              </div>

              <div className="glass p-8">
                <h3 className="font-heading text-xl text-text-primary mb-4">
                  Response Time
                </h3>
                <p className="text-text-secondary text-sm">
                  We typically respond to all inquiries within 24-48 business hours. 
                  For urgent matters, please reach out via our social channels.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
